'use client';

import { useMemo, useState } from 'react';
import Image from 'next/image';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Select, Stack, Step, StepButton, StepLabel, Stepper, TextField, Typography,
} from '@mui/material';
import { Collections, PictureAsPdf } from '@mui/icons-material';
import { useRouter } from 'next/navigation';
import {
    Enum_Historyevent_Showdateas,
    HistoryEvent,
    PublicationStatus,
    UploadFile,
} from '@/app/lib/generated/graphql-types';
import { HistoryEventSchema } from '@/app/features/sites/history/types/schema';
import { createHistoryEvent, CreateHistoryEventData } from '@/app/features/sites/history/data-access/mutations';
import { MediaBrowserV2Dialog } from '@/app/features/shared/media-browser-v2';
import {
    mediaRefAlt,
    mediaRefDimensions,
    mediaRefName,
    mediaRefUrl,
} from '@/app/features/shared/__to_be_integrated/types/general/media/media-helper';
import HistoryEventView from '@/app/features/sites/history/ui/public/HistoryEventView';

type FormData = {
    title: string;
    description: string;
    startDate: string;
    endDate: string | null;
    showDateAs: Enum_Historyevent_Showdateas;
    image: UploadFile | null;
    readMore: UploadFile | null;
};

const initialForm: FormData = {
    title: '',
    description: '',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: null,
    showDateAs: Enum_Historyevent_Showdateas.YearOnly,
    image: null,
    readMore: null,
};

const dateLabels: Record<Enum_Historyevent_Showdateas, string> = {
    [Enum_Historyevent_Showdateas.YearOnly]: 'Nur Jahr',
    [Enum_Historyevent_Showdateas.MonthAndYear]: 'Monat und Jahr',
    [Enum_Historyevent_Showdateas.DayAndYear]: 'Tag und Jahr',
};

const stepTitles = ['Inhalt', 'Bild', 'PDF', 'Preview + Approval'];

export default function AddHistoryEventDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const router = useRouter();
    const [form, setForm] = useState<FormData>(initialForm);
    const [activeStep, setActiveStep] = useState(0);
    const [completed, setCompleted] = useState<Record<number, boolean>>({});
    const [mediaStep, setMediaStep] = useState<'image' | 'readMore' | null>(null);
    const [error, setError] = useState<string>();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const previewEvent = useMemo<HistoryEvent>(() => ({
        __typename: 'HistoryEvent',
        documentId: 'preview',
        title: form.title,
        description: form.description,
        startDate: form.startDate,
        endDate: form.endDate,
        showDateAs: form.showDateAs,
        image: form.image,
        readMore: form.readMore,
        links: [],
        hasChanges: true,
        published: false,
        localizations: [],
    }), [form]);

    const resetDialog = () => {
        setForm(initialForm);
        setActiveStep(0);
        setCompleted({});
        setMediaStep(null);
        setError(undefined);
        onClose();
    };

    const close = () => {
        if (isSubmitting) return;
        resetDialog();
    };

    const validateContent = () => {
        const result = HistoryEventSchema.safeParse({
            title: form.title,
            description: form.description,
            startDate: form.startDate,
            endDate: form.endDate,
            showDateAs: form.showDateAs,
            image: form.image,
            readMore: form.readMore,
        });
        if (!result.success) {
            setError(result.error.issues[0]?.message ?? 'Bitte prüfen Sie die Eingaben.');
            return false;
        }
        setError(undefined);
        return true;
    };

    const nextStep = () => {
        if (activeStep === 0 && !validateContent()) return;
        setError(undefined);
        setCompleted((current) => ({ ...current, [activeStep]: true }));
        setActiveStep((current) => Math.min(stepTitles.length - 1, current + 1));
    };

    const previousStep = () => {
        setError(undefined);
        setActiveStep((current) => Math.max(0, current - 1));
    };

    const handleStepClick = (stepIndex: number) => {
        if (stepIndex > activeStep && !completed[stepIndex]) return;
        setError(undefined);
        setActiveStep(stepIndex);
    };

    const submit = async () => {
        if (!validateContent()) return;
        setIsSubmitting(true);
        setError(undefined);
        const data: CreateHistoryEventData = {
            title: form.title,
            description: form.description,
            startDate: form.startDate,
            endDate: form.endDate,
            showDateAs: form.showDateAs,
            image: form.image,
            readMore: form.readMore,
        };
        const result = await createHistoryEvent({ data, status: PublicationStatus.Draft });
        if (result.error || !result.data) {
            setError(result.error?.message ?? 'Das Ereignis konnte nicht angelegt werden.');
            setIsSubmitting(false);
            return;
        }
        setIsSubmitting(false);
        resetDialog();
        router.refresh();
    };

    const selectedImageName = form.image ? mediaRefName(form.image) : undefined;
    const selectedPdfName = form.readMore ? mediaRefName(form.readMore) : undefined;
    const imageDimensions = form.image ? mediaRefDimensions(form.image) : null;

    return (
        <Dialog
            open={open}
            onClose={close}
            fullWidth
            maxWidth={activeStep === 3 ? 'xl' : 'sm'}
            aria-labelledby="add-history-event-title"
            PaperProps={{
                sx: activeStep === 3 ? {
                    width: 'calc(100vw - 32px)',
                    maxWidth: 1440,
                } : undefined,
            }}
        >
            <DialogTitle id="add-history-event-title">
                Ereignis hinzufügen
                <Typography component="span" variant="body2" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                    Schritt {activeStep + 1} von {stepTitles.length}: {stepTitles[activeStep]}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Stepper activeStep={activeStep} sx={{ mb: 4 }} nonLinear aria-label="Workflow zum Hinzufügen eines Ereignisses">
                    {stepTitles.map((title, index) => (
                        <Step key={title} completed={completed[index]}>
                            <StepButton
                                color="inherit"
                                onClick={() => handleStepClick(index)}
                                disabled={index > activeStep && !completed[index]}
                            >
                                <StepLabel>{title}</StepLabel>
                            </StepButton>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 0 && (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField label="Titel" required autoFocus value={form.title}
                            onChange={(event) => setForm({ ...form, title: event.target.value })} />
                        <TextField label="Beschreibung" required multiline minRows={5} value={form.description}
                            helperText="Markdown und Zeilenumbrüche werden in der Ansicht unterstützt."
                            onChange={(event) => setForm({ ...form, description: event.target.value })} />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField label="Startdatum" required type="date" value={form.startDate}
                                onChange={(event) => setForm({ ...form, startDate: event.target.value })}
                                slotProps={{ inputLabel: { shrink: true } }} />
                            <TextField label="Enddatum (optional)" type="date" value={form.endDate ?? ''}
                                onChange={(event) => setForm({ ...form, endDate: event.target.value || null })}
                                slotProps={{ inputLabel: { shrink: true } }} />
                        </Stack>
                        <FormControl>
                            <InputLabel id="history-event-date-display-label">Datumsanzeige</InputLabel>
                            <Select labelId="history-event-date-display-label" label="Datumsanzeige" value={form.showDateAs}
                                onChange={(event) => setForm({ ...form, showDateAs: event.target.value as Enum_Historyevent_Showdateas })}>
                                {Object.entries(dateLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Stack>
                )}

                {activeStep === 1 && (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography>Ein Bild ist optional und wird links neben dem Ereignis angezeigt.</Typography>
                        {selectedImageName && <Alert severity="success">Ausgewählt: {selectedImageName}</Alert>}
                        {form.image && imageDimensions && (
                            <Box sx={{ maxWidth: 420, width: '100%' }}>
                                <Image
                                    src={mediaRefUrl(form.image, { size: 'medium' })}
                                    alt={mediaRefAlt(form.image) ?? selectedImageName ?? ''}
                                    width={imageDimensions.width}
                                    height={imageDimensions.height}
                                    sizes="(max-width: 600px) 100vw, 420px"
                                    style={{ width: '100%', height: 'auto', display: 'block' }}
                                />
                            </Box>
                        )}
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" startIcon={<Collections />} onClick={() => setMediaStep('image')}>
                                {form.image ? 'Bild ersetzen' : 'Bild auswählen'}
                            </Button>
                            {form.image && <Button color="error" onClick={() => setForm({ ...form, image: null })}>Bild entfernen</Button>}
                        </Stack>
                    </Stack>
                )}

                {activeStep === 2 && (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography>Ein PDF kann als weiterführende Information verlinkt werden.</Typography>
                        {selectedPdfName && <Alert severity="success">Ausgewählt: {selectedPdfName}</Alert>}
                        {form.readMore && (
                            <Box
                                component="iframe"
                                src={mediaRefUrl(form.readMore)}
                                title={`Vorschau von ${selectedPdfName ?? 'PDF-Dokument'}`}
                                sx={{ width: '100%', height: 320, border: 1, borderColor: 'divider' }}
                            />
                        )}
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={() => setMediaStep('readMore')}>
                                {form.readMore ? 'PDF ersetzen' : 'PDF auswählen'}
                            </Button>
                            {form.readMore && <Button color="error" onClick={() => setForm({ ...form, readMore: null })}>PDF entfernen</Button>}
                        </Stack>
                    </Stack>
                )}

                {activeStep === 3 && (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Alert severity="info">Bitte prüfen Sie die Darstellung. Sie entspricht der Ansicht nach dem Speichern.</Alert>
                        <Box sx={{ border: 1, borderColor: 'divider', p: { xs: 1, sm: 3 }, overflow: 'hidden' }}>
                            <HistoryEventView
                                event={previewEvent}
                                headingId="history-event-preview-heading"
                                animated={false}
                            />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Nach dem Anlegen wird das Ereignis als Entwurf gespeichert und kann anschließend veröffentlicht werden.
                        </Typography>
                    </Stack>
                )}
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={isSubmitting}>Abbrechen</Button>
                {activeStep > 0 && <Button onClick={previousStep} disabled={isSubmitting}>Zurück</Button>}
                {activeStep < stepTitles.length - 1 ? (
                    <Button variant="contained" onClick={nextStep}>Weiter</Button>
                ) : (
                    <Button variant="contained" onClick={submit} disabled={isSubmitting}>
                        {isSubmitting ? 'Wird angelegt …' : 'Prüfen und anlegen'}
                    </Button>
                )}
            </DialogActions>
            <MediaBrowserV2Dialog
                isOpen={mediaStep !== null}
                initialMediaTypes={mediaStep === 'image' ? ['image/*'] : mediaStep === 'readMore' ? ['application/pdf'] : undefined}
                onApply={(media) => {
                    if (mediaStep === 'image') {
                        if (media && !media.mime?.startsWith('image/')) {
                            setError('Bitte eine Bilddatei auswählen.');
                            return;
                        }
                        setForm({ ...form, image: media });
                    } else if (mediaStep === 'readMore') {
                        if (media && media.mime !== 'application/pdf') {
                            setError('Bitte eine PDF-Datei auswählen.');
                            return;
                        }
                        setForm({ ...form, readMore: media });
                    }
                    setMediaStep(null);
                    setError(undefined);
                }}
                onClose={() => setMediaStep(null)}
            />
        </Dialog>
    );
}
